import { ORBIT_LAYER_IDS } from './mapLayerOrder';
import { SemanticZoomLevel } from './semanticZoom';

// Semantic zoom levels are numbered from widest to most detailed:
// 5 = Network Overview, 1 = Fittings.
// A layer is visible when the current level falls within its [last, first] band:
//   visible = currentLevel <= firstVisibleAt && currentLevel >= lastVisibleAt
// firstVisibleAt is the widest level the layer appears at; lastVisibleAt (default
// Fittings, the most detailed) is how far in it persists. Most layers persist all the
// way in and only declare a first-visible level. Layers that should disappear as you
// zoom past their band also declare a last-visible level (see below).
// Layers absent from both maps are not controlled by semantic zoom (always visible).
const LAYER_FIRST_VISIBLE_AT_LEVEL: Readonly<Partial<Record<string, SemanticZoomLevel>>> = {
    // Network Overview: grid tiles + surveyed areas provide overview context.
    // networkBoundary hook is deferred to a future PBI; mapping is ready for when it arrives.
    [ORBIT_LAYER_IDS.networkBoundary]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.gridTilesFill]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.gridTilesOutline]: SemanticZoomLevel.NetworkOverview,
    // Grid tile labels share the Network Overview level with the tile geometry so the
    // labels are always shown alongside the tiles they belong to.
    [ORBIT_LAYER_IDS.gridTilesLabels]: SemanticZoomLevel.NetworkOverview,
    // Surveyed areas first appear at Network Overview and persist through Area of Interest.
    [ORBIT_LAYER_IDS.surveyedAreasFill]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.surveyedAreasOutline]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.areaLabels]: SemanticZoomLevel.NetworkOverview,
    // District Meter Area: DMAs first appear here (and persist through Pipelines).
    [ORBIT_LAYER_IDS.dmaFill]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.dmaOutline]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.dmaLabels]: SemanticZoomLevel.DistrictMeterArea,
    // POI markers belong to the District Meter Area level and persist all the way in to Fittings.
    // Within the band their layer minzoom holds them back to the band's mid stop, where the
    // bubbles hand over; the registry works at band level only. The split-model leak markers
    // and the shared-origin fan base share the POI band — the two families appear and
    // disappear together.
    [ORBIT_LAYER_IDS.pois]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.leaks]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.entityFanBase]: SemanticZoomLevel.DistrictMeterArea,
    // Network-level POI bubbles (backed by the lightweight all-network source) cover the
    // wide end: Network Overview through the outer half of District Meter Area, handing over
    // to the viewport POI layers at that band's mid stop (see LAYER_LAST_VISIBLE_AT_LEVEL;
    // the mid-band cut itself is the layers' maxzoom).
    [ORBIT_LAYER_IDS.poiNetworkClusters]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.poiNetworkClusterCount]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.poiNetworkUnclustered]: SemanticZoomLevel.NetworkOverview,
    // The Network Explorer's cell bubbles are the explorer's wide end and hand over to the
    // same viewport POI layers at the same boundary, so they share the network clusters' band.
    [ORBIT_LAYER_IDS.explorerClusters]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.explorerClusterCount]: SemanticZoomLevel.NetworkOverview,
    // SAVI air valves are the SAVI equivalent of POIs and share their band (District Meter Area
    // inward). Their cluster circles/counts share the same band so they appear/disappear together.
    [ORBIT_LAYER_IDS.airValves]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.airValvesHighRisk]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.airValvesClusters]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.airValvesClusterCount]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.airValvesHighRiskClusters]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.airValvesHighRiskClusterCount]: SemanticZoomLevel.DistrictMeterArea,
    // Pipelines
    [ORBIT_LAYER_IDS.pipelinesCase]: SemanticZoomLevel.Pipelines,
    [ORBIT_LAYER_IDS.pipelines]: SemanticZoomLevel.Pipelines,
    // Fittings
    [ORBIT_LAYER_IDS.fittingsShadow]: SemanticZoomLevel.Fittings,
    [ORBIT_LAYER_IDS.fittings]: SemanticZoomLevel.Fittings,
};

// Layers that stop being visible once you zoom in past their band (layers absent here persist to
// Fittings). Grid tiles are Network Overview overview context only. Surveyed areas persist one level
// further, through Area of Interest. DMAs turn off at Fittings so they don't clash with the dense
// street-level detail (and share their blue styling with the grid overview at the coarse end).
const LAYER_LAST_VISIBLE_AT_LEVEL: Readonly<Partial<Record<string, SemanticZoomLevel>>> = {
    [ORBIT_LAYER_IDS.gridTilesFill]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.gridTilesOutline]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.gridTilesLabels]: SemanticZoomLevel.NetworkOverview,
    [ORBIT_LAYER_IDS.surveyedAreasFill]: SemanticZoomLevel.AreaOfInterest,
    [ORBIT_LAYER_IDS.surveyedAreasOutline]: SemanticZoomLevel.AreaOfInterest,
    [ORBIT_LAYER_IDS.areaLabels]: SemanticZoomLevel.AreaOfInterest,
    [ORBIT_LAYER_IDS.dmaFill]: SemanticZoomLevel.Pipelines,
    [ORBIT_LAYER_IDS.dmaOutline]: SemanticZoomLevel.Pipelines,
    [ORBIT_LAYER_IDS.dmaLabels]: SemanticZoomLevel.Pipelines,
    // Network-level POI bubbles (and the explorer's cells) share the District Meter Area band
    // with the markers: the bubbles' layer maxzoom and the markers' minzoom meet at the band's
    // mid stop, so within the band exactly one of the two is drawn at any zoom. Both turn off
    // at Pipelines, where only the markers continue.
    [ORBIT_LAYER_IDS.poiNetworkClusters]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.poiNetworkClusterCount]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.poiNetworkUnclustered]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.explorerClusters]: SemanticZoomLevel.DistrictMeterArea,
    [ORBIT_LAYER_IDS.explorerClusterCount]: SemanticZoomLevel.DistrictMeterArea,
};

/**
 * Returns the widest semantic zoom level at which a layer first becomes visible, or
 * undefined for layers not controlled by semantic zoom. Callers use this to fly to the
 * level at which a feature would appear (e.g. the AOI/DMA stats-card "View" action).
 */
export function getLayerFirstVisibleLevel(layerId: string): SemanticZoomLevel | undefined {
    return LAYER_FIRST_VISIBLE_AT_LEVEL[layerId];
}

export function isLayerVisibleAtSemanticLevel(layerId: string, currentLevel: SemanticZoomLevel): boolean {
    const firstVisibleAt = LAYER_FIRST_VISIBLE_AT_LEVEL[layerId];
    if (firstVisibleAt === undefined) return true; // uncontrolled — always visible
    const lastVisibleAt = LAYER_LAST_VISIBLE_AT_LEVEL[layerId] ?? SemanticZoomLevel.Fittings;
    return currentLevel <= firstVisibleAt && currentLevel >= lastVisibleAt;
}
